/** @jsxImportSource @emotion/react */
import React, { useState, useId } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { Button, Alert, theme } from 'antd';
import {
  ArrowRightOutlined,
  LockOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { getAuth } from 'firebase/auth';
import { signOut } from '../../~reusables/firebase/auth';

export const AccessNotice: React.FC<{
  title: string;
  description: string;
  hint?: string;
  backTo?: string;
  backLabel?: string;
  onRetry?: () => void;
}> = ({
  title,
  description,
  hint = 'Need access? Ask one of the team’s Admins to invite you.',
  backTo = '/',
  backLabel = 'Back to all apps',
  onRetry,
}) => {
  const titleId = useId();
  const { token } = theme.useToken();
  const history = useHistory();
  const location = useLocation();
  const email = getAuth().currentUser?.email;
  const [switching, setSwitching] = useState(false);
  const [switchError, setSwitchError] = useState(false);

  const switchAccount = async () => {
    setSwitching(true);
    setSwitchError(false);
    const returnTo = `${location.pathname}${location.search}${location.hash}`;
    try {
      await signOut();
      history.replace(`/login?redirectTo=${encodeURIComponent(returnTo)}`);
    } catch {
      setSwitchError(true);
      setSwitching(false);
    }
  };

  return (
    <div
      css={{
        'display': 'flex',
        'justifyContent': 'center',
        'padding': '48px 0',
        '@media (max-width: 520px)': { padding: '16px 0' },
      }}
    >
      <section
        aria-labelledby={titleId}
        css={{
          'width': '100%',
          'maxWidth': 530,
          'padding': 24,
          'border': `1px solid ${token.colorBorderSecondary}`,
          'borderRadius': 10,
          'background': token.colorBgContainer,
          '@media (max-width: 520px)': { padding: 20 },
        }}
      >
        <div
          css={{
            'display': 'flex',
            'gap': 14,
            'alignItems': 'flex-start',
            '@media (max-width: 420px)': { flexDirection: 'column' },
          }}
        >
          <div
            aria-hidden="true"
            css={{
              width: 39,
              height: 39,
              flexShrink: 0,
              borderRadius: 10,
              background: token.colorPrimaryBg,
              color: token.colorPrimary,
              display: 'grid',
              placeItems: 'center',
              fontSize: 19,
            }}
          >
            <LockOutlined />
          </div>
          <div>
            <h1
              id={titleId}
              css={{
                margin: '0 0 7px',
                fontSize: 20,
                lineHeight: 1.35,
                fontWeight: 500,
                color: token.colorText,
              }}
            >
              {title}
            </h1>
            <p
              css={{
                margin: 0,
                fontSize: 14,
                lineHeight: 1.6,
                color: token.colorTextSecondary,
              }}
            >
              {description}
            </p>
          </div>
        </div>
        <div
          css={{
            border: `1px solid ${token.colorBorderSecondary}`,
            borderRadius: 8,
            padding: 12,
            marginTop: 21,
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 10,
          }}
        >
          <div
            aria-hidden="true"
            css={{
              width: 32,
              height: 32,
              flexShrink: 0,
              borderRadius: '50%',
              background: token.colorFillQuaternary,
              color: token.colorTextSecondary,
              display: 'grid',
              placeItems: 'center',
            }}
          >
            <UserOutlined />
          </div>
          <div css={{ flex: '1 1 150px', minWidth: 0 }}>
            <div css={{ fontSize: 12, color: token.colorTextSecondary }}>
              {email ? 'You’re signed in as' : 'You’re signed in'}
            </div>
            {email && (
              <div
                css={{
                  fontSize: 13,
                  fontWeight: 500,
                  overflowWrap: 'anywhere',
                  color: token.colorText,
                }}
              >
                {email}
              </div>
            )}
          </div>
          <Button
            type="link"
            loading={switching}
            onClick={switchAccount}
            css={{ paddingInline: 4 }}
          >
            Switch account
          </Button>
        </div>
        {switchError && (
          <Alert
            role="alert"
            type="error"
            showIcon
            title="Couldn’t sign out. Please try again."
            css={{ marginTop: 16 }}
          />
        )}
        <div
          css={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 16,
            marginTop: 22,
          }}
        >
          {onRetry ? (
            <Button onClick={onRetry}>Try again</Button>
          ) : (
            <p
              css={{
                margin: 0,
                maxWidth: 185,
                fontSize: 12,
                lineHeight: 1.5,
                color: token.colorTextSecondary,
              }}
            >
              {hint}
            </p>
          )}
          <Link to={backTo}>
            <Button type="primary">
              {backLabel} <ArrowRightOutlined />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};
